/**
 * One avatar for everyone: a photo when there is one, otherwise initials.
 * PatientAvatar loads a patient's photo on its own (cached per patient), so
 * lists that only know the patient's id and name still show the photo.
 */
import { useState } from "react";
import { usePatientPhoto } from "@/hooks/use-patient-photo";
import { cn } from "@/lib/utils";

const SIZES = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-base",
  xl: "h-24 w-24 text-3xl",
} as const;

export type AvatarSize = keyof typeof SIZES;

function initialsOf(name?: string | null): string {
  const parts = (name ?? "").replace(/^(dr|mr|mrs|ms|prof)\.?\s+/i, "").trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
  return (first + last).toUpperCase() || "?";
}

export function PersonAvatar({ src, name, initials, size = "md", className, tone = "coral" }: {
  src?: string | null;
  name?: string | null;
  /** Override the initials worked out from the name. */
  initials?: string;
  size?: AvatarSize;
  className?: string;
  /** coral = staff/default, pine = patients, light = on dark backgrounds */
  tone?: "coral" | "pine" | "light";
}) {
  const [failed, setFailed] = useState<string | null>(null);
  const showImage = Boolean(src) && failed !== src;
  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold",
        SIZES[size],
        !showImage && tone === "coral" && "bg-[#ff8d6e] text-[#003a43]",
        !showImage && tone === "pine" && "bg-[#e6f0ee] text-[#004952] dark:bg-muted dark:text-foreground",
        !showImage && tone === "light" && "bg-white/15 text-white ring-1 ring-white/25",
        className,
      )}
      aria-hidden
    >
      {showImage ? (
        <img src={src!} alt="" className="h-full w-full object-cover" onError={() => setFailed(src ?? null)} />
      ) : (
        initials ?? initialsOf(name)
      )}
    </span>
  );
}

/**
 * A patient's avatar. Pass photoUrl when the list already has it; otherwise
 * the photo is fetched once per patient and reused everywhere.
 */
export function PatientAvatar({ patientId, name, photoUrl, size = "md", className }: {
  patientId?: string | null;
  name?: string | null;
  photoUrl?: string | null;
  size?: AvatarSize;
  className?: string;
}) {
  const src = usePatientPhoto(patientId, photoUrl);
  return <PersonAvatar src={src} name={name} size={size} className={className} tone="pine" />;
}
